// _elements/blocks/BlockRenderer.tsx
"use client";

import React from "react";
import styled from "styled-components";
import type { ContentBlock, TableData, ListItem } from "../../_sys/_types";
import { inlineMarkdown } from "./shared";

import {
  StatGrid,
  TagMosaic,
  BadgeRowFromBlock,
} from "../primitives/DataDisplay";
import {
  StatusCard,
  StepTrackerFromBlock,
} from "../primitives/StatusAndProgress";
import { PricingTierGridFromBlock } from "../primitives/PricingTierGrid";
import { MockUpFrame } from "../primitives/MockUpFrame";
import { ComparisonChart } from "../primitives/ComparisonChart";
import { EnvelopeInspector } from "../primitives/EnvelopeInspector";

// ============================================================
// PARAGRAPH / LIST / TABLE — the plain-content fallbacks.
// Styled to match the existing _sections/index.tsx conventions
// exactly, so mixing legacy and block content looks seamless.
// ============================================================

const Paragraph = styled.p`
  font-size: ${({ theme }) => theme.typography.sizes.body};
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-family: ${({ theme }) => theme.typography.fonts.semibold};
  }
  em {
    font-family: ${({ theme }) => theme.typography.fonts.regularItalic};
  }
`;

const List = styled.ul<{ $variant?: "bullet" | "number" | "check" }>`
  list-style: ${(props) =>
    props.$variant === "number"
      ? "decimal"
      : props.$variant === "check"
        ? "none"
        : "disc"};
  padding-left: ${(props) =>
    props.$variant === "check" ? "0" : props.theme.spacing.large};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
`;

const ListItemStyled = styled.li<{ $check?: boolean }>`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.tight};
  padding-left: ${(props) => (props.$check ? "28px" : "0")};
  position: relative;

  &::before {
    content: ${(props) => (props.$check ? '"✓"' : "none")};
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-family: ${({ theme }) => theme.typography.fonts.bold};
  }

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-family: ${({ theme }) => theme.typography.fonts.semibold};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  background: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const TableHeaderCell = styled.th`
  background: ${({ theme }) => theme.colors.secondaryBackground};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  text-align: left;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.primary};
  border-bottom: ${({ theme }) => theme.borders.width.medium} solid
    ${({ theme }) => theme.gradients.glass};
`;

const TableDataCell = styled.td`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-bottom: ${({ theme }) => theme.borders.width.thin} solid
    ${({ theme }) => theme.colors.secondaryBackground};
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:first-child {
    font-family: ${({ theme }) => theme.typography.fonts.medium};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const TableRowStyled = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
  &:hover {
    background: ${({ theme }) => theme.gradients.secondary};
  }
`;

const TableCaption = styled.p`
  margin: -${({ theme }) => theme.spacing.small} 0
    ${({ theme }) => theme.spacing.medium} 0;
  font-family: ${({ theme }) => theme.typography.fonts.regularItalic};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const RenderTable: React.FC<{ table: TableData; caption?: string }> = ({
  table,
  caption,
}) => (
  <>
    <Table>
      <thead>
        <tr>
          {table.headers.map((header, idx) => (
            <TableHeaderCell key={idx}>{header}</TableHeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIdx) => (
          <TableRowStyled key={rowIdx}>
            {row.cells.map((cell, cellIdx) => (
              <TableDataCell key={cellIdx}>{cell}</TableDataCell>
            ))}
          </TableRowStyled>
        ))}
      </tbody>
    </Table>
    {caption && <TableCaption>{caption}</TableCaption>}
  </>
);

export const RenderList: React.FC<{
  items: ListItem[];
  variant?: "bullet" | "number" | "check";
}> = ({ items, variant }) => {
  const resolvedVariant = variant || items[0]?.type || "bullet";
  return (
    <List $variant={resolvedVariant}>
      {items.map((item, idx) => (
        <ListItemStyled key={idx} $check={resolvedVariant === "check"}>
          {item.label && <strong>{item.label} </strong>}
          {item.text}
        </ListItemStyled>
      ))}
    </List>
  );
};

// ============================================================
// BLOCK RENDERER
// ============================================================

const BlocksWrapper = styled.div``;

/**
 * Renders an ordered list of ContentBlocks. This is the single place
 * that knows how a `block.type` maps to a component — every section
 * (Overview, Features, TechSubsection, Process) calls this the same
 * way, so a new visual type only needs to be added here once.
 */
export const BlockRenderer: React.FC<{ blocks?: ContentBlock[] }> = ({
  blocks,
}) => {
  if (!blocks || blocks.length === 0) return null;

  return (
    <BlocksWrapper>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "paragraph":
            return (
              <Paragraph
                key={idx}
                dangerouslySetInnerHTML={{ __html: inlineMarkdown(block.text) }}
              />
            );
          case "bulletList":
            return (
              <RenderList
                key={idx}
                items={block.items}
                variant={block.variant}
              />
            );
          case "table":
            return (
              <RenderTable
                key={idx}
                table={block.table}
                caption={block.caption}
              />
            );
          case "statGrid":
            return <StatGrid key={idx} block={block} />;
          case "tagMosaic":
            return <TagMosaic key={idx} block={block} />;
          case "mockupFrame":
            return <MockUpFrame key={idx} block={block} />;
          case "statusCard":
            return <StatusCard key={idx} block={block} />;
          case "stepTracker":
            return <StepTrackerFromBlock key={idx} block={block} />;
          case "pricingTiers":
            return <PricingTierGridFromBlock key={idx} block={block} />;
          case "badgeRow":
            return <BadgeRowFromBlock key={idx} block={block} />;
          case "comparisonChart":
            return <ComparisonChart key={idx} block={block} />;
          case "codeOrEnvelope":
            return <EnvelopeInspector key={idx} block={block} />;
          case "scene3d":
            // scene3d is intentionally NOT rendered inline in a content
            // flow — it's a background accent meant for hero/CTA only.
            // Consuming it here would place a full-height Canvas inline
            // in a feature list. Sections that support it (Hero, CTA)
            // render <Scene3D> directly instead of routing it through
            // BlockRenderer. Unknown/misplaced usage is a silent no-op.
            return null;
          default:
            // Forward-compatible: unrecognized block types (future
            // additions from newer product JSON) are skipped rather
            // than crashing older deployed component code.
            return null;
        }
      })}
    </BlocksWrapper>
  );
};
